/** English. Keys that are missing here fall back to Dutch. */
import type { Woordenboek } from '../index.ts';

export const en: Partial<Woordenboek> = {
  'app.naam': 'Mizen',
  'algemeen.opslaan': 'Save',
  'algemeen.annuleren': 'Cancel',
  'algemeen.sluiten': 'Close',
  'algemeen.verwijderen': 'Delete',
  'algemeen.bewerken': 'Edit',
  'algemeen.terug': 'Back',
  'algemeen.volgende': 'Next',
  'algemeen.zoeken': 'Search',
  'algemeen.laden': 'Loading',
  'algemeen.leeg': 'Nothing here yet',
  'algemeen.fout': 'Something went wrong',
  'algemeen.opnieuw': 'Try again',
  'algemeen.totaal': 'Total',
  'algemeen.datum': 'Date',
  'algemeen.bedrag': 'Amount',
  'algemeen.omschrijving': 'Description',
  'algemeen.status': 'Status',
  'algemeen.aantal': 'Quantity',
  'algemeen.prijs': 'Price',
  'algemeen.btw': 'VAT',
  'algemeen.meer': 'More',
  'algemeen.van': 'from',
  'algemeen.tot': 'up to and including',
  'algemeen.periode': 'Period',
  'algemeen.ja': 'Yes',
  'algemeen.nee': 'No',
  'algemeen.verplicht': 'This field is required',
  'algemeen.exporteren': 'Export',
  'algemeen.downloaden': 'Download',

  'nav.dashboard': 'Overview',
  'nav.verkoop': 'Invoices',
  'nav.uren': 'Time tracking',
  'nav.inkoop': 'Receipts and purchases',
  'nav.bank': 'Bank',
  'nav.relaties': 'Customers and suppliers',
  'nav.rapporten': 'Figures',
  'nav.instellingen': 'Settings',

  // Korte varianten voor de tabbalk op een telefoon, waar de volledige naam
  // niet leesbaar past.
  'nav.kort.dashboard': 'Home',
  'nav.kort.verkoop': 'Invoices',
  'nav.kort.uren': 'Time',
  'nav.kort.inkoop': 'Purchases',
  'nav.kort.bank': 'Bank',
  'nav.kort.relaties': 'Contacts',
  'nav.kort.rapporten': 'Figures',
  'nav.kort.instellingen': 'Settings',
  'nav.hoofdmenu': 'Main menu',
  'nav.naarInhoud': 'Skip to content',
  'nav.administratieKiezen': 'Choose a company',
  'nav.afmelden': 'Sign out',
  // Test environment and feedback
  'omgeving.test': 'Test environment',
  'omgeving.testUitleg':
    'This is a preview for you to look at and judge. The data in it is made up; do not put a real administration in here.',
  'feedback.knop': 'Noticed something?',
  'feedback.titel': 'What stands out?',
  'feedback.uitleg':
    'Write down whatever you notice, however small. We record which screen you were on, so you do not have to explain that.',
  'feedback.soort': 'What is it?',
  'feedback.soort.opmerking': 'A remark',
  'feedback.soort.fout': 'Something goes wrong',
  'feedback.soort.wens': 'Something I would like',
  'feedback.soort.vraag': 'A question',
  'feedback.bericht': 'Your remark',
  'feedback.naam': 'Your name',
  'feedback.naamUitleg': 'Only if you want us to be able to come back to you.',
  'feedback.versturen': 'Send',
  'feedback.verstuurd': 'Thank you. Your remark has been noted.',
  'feedback.overzicht': 'Remarks',
  'feedback.leeg': 'No remarks yet.',
  'feedback.leegUitleg': 'As soon as somebody notices something, it arrives here.',
  'feedback.scherm': 'Screen',
  'feedback.wie': 'From',
  'feedback.wanneer': 'When',
  'feedback.antwoord': 'Reply',
  'feedback.antwoordPlaatsen': 'Note a reply',
  'feedback.status.nieuw': 'New',
  'feedback.status.opgepakt': 'Picked up',
  'feedback.status.verwerkt': 'Done',
  'feedback.status.afgewezen': 'Not doing',
  'feedback.aantalNieuw': 'new',
  'feedback.alles': 'All',

  'auth.aanmelden': 'Sign in',
  'auth.registreren': 'Create an account',
  'auth.email': 'Email address',
  'auth.wachtwoord': 'Password',
  'auth.naam': 'Your name',
  'auth.wachtwoordEis': 'At least 12 characters. A short sentence works well and is easy to remember.',
  'auth.nogGeenAccount': 'No account yet? Create one.',
  'auth.welAccount': 'Already have an account? Sign in.',
  'auth.mfaTitel': 'One more step',
  'auth.mfaUitleg':
    'Enter the six-digit code from your authenticator app. No app at hand? Use a recovery code.',
  'auth.mfaCode': 'Code',
  'auth.mfaBevestigen': 'Confirm',
  'auth.mfaInstellen': 'Set up two-step verification',
  'auth.mfaUitleglang':
    'With two-step verification, your password alone is not enough to reach your books. Scan the code with an authenticator app on your phone.',
  'auth.mfaGeheim': 'Cannot scan? Enter this key manually:',
  'auth.mfaHerstelcodes': 'Recovery codes',
  'auth.mfaHerstelcodesUitleg':
    'Keep these somewhere safe, such as a password manager. Each code works once and lets you sign in if you lose your phone.',
  'auth.mfaAan': 'Two-step verification is on',
  'auth.mfaUit': 'Two-step verification is off',
  'auth.sessies': 'Devices you are signed in on',
  'auth.sessiesIntrekken': 'Sign out other devices',
  'auth.registratieGelukt': 'Your account has been created. You can sign in now.',

  'org.nieuweOrganisatie': 'New organisation',
  'org.naam': 'Organisation name',
  'org.kvk': 'Chamber of Commerce number',
  'org.abonnement': 'Plan',
  'admin.nieuw': 'New company',
  'admin.naam': 'Company name',
  'admin.rechtsvorm': 'Legal form',
  'admin.schema': 'Chart of accounts',
  'admin.schemaUitleg':
    'The chart of accounts decides where each amount ends up. We prepare a complete Dutch chart for you; you can change it at any time.',
  'admin.btwNummer': 'VAT identification number',
  'admin.adres': 'Address',
  'admin.postcodePlaats': 'Postcode and city',
  'admin.iban': 'IBAN',
  'admin.boekjaar': 'Financial year',
  'admin.kiezen': 'Choose a company',
  'admin.geenAdministraties': 'You do not have a company yet. Create one to get started.',
  'admin.actief': 'You are working in',

  'dashboard.titel': 'How is your business doing?',
  'dashboard.omzet': 'Revenue',
  'dashboard.omzetUitleg': 'Everything you earned from your work or products, excluding VAT.',
  'dashboard.kosten': 'Costs',
  'dashboard.kostenUitleg': 'What you spent to do your work, excluding VAT.',
  'dashboard.winst': 'Profit',
  'dashboard.winstUitleg': 'Revenue minus costs. This is what you are ultimately taxed on.',
  'dashboard.banksaldo': 'In the bank',
  'dashboard.banksaldoUitleg':
    'The balance according to your books. It can differ from your banking app while transactions are still unprocessed.',
  'dashboard.teOntvangen': 'Still to receive',
  'dashboard.teOntvangenUitleg': 'Money customers still owe you.',
  'dashboard.teBetalen': 'Still to pay',
  'dashboard.teBetalenUitleg': 'Money you still owe suppliers.',
  'dashboard.btw': 'Expected VAT',
  'dashboard.btwUitleg':
    'What you are expected to pay or reclaim for this period. This is a calculation, not a filing.',
  'dashboard.aandacht': 'This needs your attention',
  'dashboard.teBoeken': 'Bank transactions still to process',
  'dashboard.ontbrekendeBonnen': 'Expenses without a receipt',
  'dashboard.geenAandacht': 'Everything is up to date. Nice work.',
  'dashboard.vergelijking': 'compared with the previous period',

  'facturen.titel': 'Invoices',
  'facturen.nieuw': 'New invoice',
  'facturen.nieuweOfferte': 'New quote',
  'facturen.nummer': 'Number',
  'facturen.klant': 'Customer',
  'facturen.factuurdatum': 'Invoice date',
  'facturen.vervaldatum': 'Pay by',
  'facturen.regels': 'What are you delivering?',
  'facturen.regelToevoegen': 'Add a line',
  'facturen.subtotaal': 'Subtotal',
  'facturen.totaal': 'Total due',
  'facturen.definitiefMaken': 'Finalise',
  'facturen.definitiefUitleg':
    'The invoice then gets a number and enters your books. It can no longer be changed; corrections are made with a credit note.',
  'facturen.versturen': 'Send to the customer',
  'facturen.pdf': 'View PDF',
  'facturen.ubl': 'Download UBL',
  'facturen.crediteren': 'Create credit note',
  'facturen.status.concept': 'Draft',
  'facturen.status.definitief': 'Final',
  'facturen.status.verzonden': 'Sent',
  'facturen.status.deels_betaald': 'Partly paid',
  'facturen.status.betaald': 'Paid',
  'facturen.status.vervallen': 'Overdue',
  'facturen.status.geannuleerd': 'Cancelled',
  'facturen.leeg': 'You have not created any invoices yet.',
  'facturen.leegUitleg': 'Create your first invoice; we will make sure it lands correctly in your books.',
  'facturen.referentie': 'Customer reference',
  'facturen.notitie': 'Note on the invoice',
  'facturen.openstaand': 'Still outstanding',
  // Invoice overview: filters and totals
  'facturen.zoeken': 'Search by number, customer or reference',
  'facturen.alles': 'All',
  'facturen.alleSoorten': 'All types',
  'facturen.alleStatussen': 'All invoices',
  'facturen.alleenOpenstaand': 'Outstanding only',
  'facturen.alleenVervallen': 'Overdue only',
  'facturen.aantalGevonden': 'found',
  'facturen.totaalBedrag': 'Total invoiced',
  'facturen.totaalOpenstaand': 'Still to receive',
  'facturen.totaalVervallen': 'Of which overdue',
  'facturen.meerLaden': 'Load more',
  'facturen.geenResultaat': 'No invoices found',
  'facturen.geenResultaatUitleg': 'Adjust your search or your filters.',
  'facturen.filtersWissen': 'Clear filters',
  'facturen.dagenTeLaat': 'days overdue',
  'facturen.sorteerOp': 'Sort by',
  'facturen.soort': 'Type',
  'facturen.soort.offerte': 'Quote',
  'facturen.soort.factuur': 'Invoice',
  'facturen.soort.creditnota': 'Credit note',
  'facturen.soort.proforma': 'Proforma',
  'facturen.status.vervallen_offerte': 'Expired quote',

  // Time tracking and projects
  'uren.titel': 'Time',
  'uren.projecten': 'Projects',
  'uren.weekstaat': 'My hours',
  'uren.nieuw': 'Log time',
  'uren.project': 'Project',
  'uren.activiteit': 'Activity',
  'uren.geenActiviteit': 'No activity',
  'uren.wie': 'Who',
  'uren.duur': 'Duration',
  'uren.minuten': 'minutes',
  'uren.uur': 'hours',
  'uren.factureerbaar': 'Billable',
  'uren.tarief': 'Hourly rate',
  'uren.urenLabel': 'Hours',
  'uren.minutenLabel': 'Minutes',
  'uren.wachtOpGoedkeuring': 'awaiting approval',
  'uren.opslaan': 'Save time entry',
  'uren.leeg': 'No time has been logged yet.',
  'uren.legUitleg': 'Log your first hours; you can invoice them together later.',
  'uren.alleenEigen': 'You only see your own hours.',
  'uren.totaalDezeWeek': 'This period',
  'uren.indienen': 'Submit',
  'uren.goedkeuren': 'Approve',
  'uren.afkeuren': 'Reject',
  'uren.afkeurreden': 'Why are you rejecting these hours?',
  'uren.factureren': 'Invoice hours',
  'uren.factureerUitleg':
    'We turn the approved hours into a draft invoice. You check it yourself before making it final.',
  'uren.selectie': 'selected',
  'uren.selecteerAlles': 'Select all',
  'uren.status.concept': 'Draft',
  'uren.status.ingediend': 'Submitted',
  'uren.status.goedgekeurd': 'Approved',
  'uren.status.afgekeurd': 'Rejected',
  'uren.status.gefactureerd': 'Invoiced',
  'uren.eigenUrenNietGoedkeuren': 'Someone else reviews your own hours.',

  'projecten.titel': 'Projects',
  'projecten.nieuw': 'New project',
  'projecten.naam': 'Project name',
  'projecten.code': 'Project code',
  'projecten.klant': 'Customer',
  'projecten.geenKlant': 'No customer',
  'projecten.facturatie': 'How is this billed?',
  'projecten.facturatie.uurtarief': 'Hourly rate',
  'projecten.facturatie.vaste_prijs': 'Fixed price',
  'projecten.facturatie.niet': 'Not billed',
  'projecten.uurtarief': 'Hourly rate',
  'projecten.vastePrijs': 'Fixed price',
  'projecten.budget': 'Time budget',
  'projecten.budgetUren': 'hours budgeted',
  'projecten.geschreven': 'Logged',
  'projecten.teFactureren': 'Still to invoice',
  'projecten.gefactureerd': 'Invoiced',
  'projecten.status.actief': 'Active',
  'projecten.status.op_pauze': 'Paused',
  'projecten.status.afgerond': 'Completed',
  'projecten.status.gearchiveerd': 'Archived',
  'projecten.leeg': 'There are no projects yet.',
  'projecten.leegUitleg': 'Create a project; then you can log hours on it and invoice them.',
  'projecten.activiteiten': 'Activities',
  'projecten.activiteitToevoegen': 'Add activity',
  'projecten.budgetOp': 'The budget is almost used up.',
  'projecten.budgetOver': 'The budget has been exceeded.',

  'relaties.titel': 'Customers and suppliers',
  'relaties.nieuw': 'New contact',
  'relaties.naam': 'Name',
  'relaties.soort': 'Type',
  'relaties.klant': 'Customer',
  'relaties.leverancier': 'Supplier',
  'relaties.beide': 'Both',
  'relaties.email': 'Email address',
  'relaties.telefoon': 'Phone',
  'relaties.btwNummer': 'VAT number',
  'relaties.kvk': 'Chamber of Commerce number',
  'relaties.iban': 'IBAN',
  'relaties.betalingstermijn': 'Payment term in days',
  'relaties.leeg': 'You have no customers or suppliers yet.',
  'relaties.dubbelWaarschuwing': 'A contact with a similar name already exists: {namen}.',
  'relaties.tochAanmaken': 'It really is a different party, create anyway',

  'inkoop.titel': 'Receipts and purchase invoices',
  'inkoop.nieuw': 'Record a receipt or invoice',
  'inkoop.leverancier': 'Supplier',
  'inkoop.factuurnummer': "Supplier's invoice number",
  'inkoop.document': 'Receipt or invoice (PDF or photo)',
  'inkoop.documentUitleg':
    'Always keep the original document. That is a legal requirement and you will need it during an audit.',
  'inkoop.leeg': 'You have not recorded any purchase invoices yet.',
  'inkoop.dubbel': 'This supplier has already sent this invoice number.',

  'bank.titel': 'Bank',
  'bank.importeren': 'Import a statement',
  'bank.importUitleg': 'Download a statement from your bank (CSV, MT940 or CAMT.053) and drop it here.',
  'bank.teVerwerken': 'Still to process',
  'bank.verwerkt': 'Processed',
  'bank.voorstel': 'Suggestion',
  'bank.voorstelWaarom': 'Why this suggestion?',
  'bank.koppelen': 'Match',
  'bank.boeken': 'Post',
  'bank.opRekening': 'Post to an account',
  'bank.reconciliatie': 'Does my bank balance match?',
  'bank.saldoGrootboek': 'According to the books',
  'bank.saldoAfschrift': 'According to the latest statement',
  'bank.verschil': 'Difference',
  'bank.sluitAan': 'Your bank balance matches your books.',
  'bank.sluitNietAan': 'There is still a difference between your statement and your books.',
  'bank.leeg': 'There are no bank transactions yet. Import a statement to get started.',
  'bank.geimporteerd': '{toegevoegd} new transactions added, {overgeslagen} were already there.',

  'rapport.titel': 'Figures',
  'rapport.balans': 'Balance sheet',
  'rapport.balansUitleg': 'What your business owns and owes on a given day.',
  'rapport.winstEnVerlies': 'Profit and loss',
  'rapport.winstEnVerliesUitleg': 'What came in and went out over a period.',
  'rapport.saldibalans': 'Trial balance',
  'rapport.grootboek': 'General ledger',
  'rapport.journaal': 'Journal',
  'rapport.ouderdom': 'Who still has to pay?',
  'rapport.btw': 'VAT overview',
  'rapport.activa': 'Assets',
  'rapport.passiva': 'Liabilities and equity',
  'rapport.opbrengsten': 'Income',
  'rapport.kosten': 'Costs',
  'rapport.resultaat': 'Result',
  'rapport.inBalans': 'The balance sheet balances.',
  'rapport.nietInBalans': 'Careful: the balance sheet does not balance. Please contact support.',
  'rapport.doorklikken': 'Click an amount to see where it comes from.',
  'rapport.vorigePeriode': 'Previous period',
  'rapport.btwVoorbehoud':
    'This overview is a calculation based on your records and is not tax advice. Check the outcome, or have an accountant or tax adviser review it before filing.',
  'rapport.btwTeBetalen': 'To pay',
  'rapport.btwTeVorderen': 'To reclaim',
  'rapport.btwSaldo': 'Net',
  'rapport.btwAansluiting': 'Reconciliation with the ledger',
  'rapport.btwSluitAan': 'The overview matches your ledger exactly.',
  'rapport.geenGegevens': 'There are no entries in this period yet.',

  'instellingen.titel': 'Settings',
  'instellingen.administratie': 'Your company details',
  'instellingen.gebruikers': 'Who has access?',
  'instellingen.beveiliging': 'Security',
  'instellingen.taal': 'Language',
  'instellingen.thema': 'Appearance',
  'instellingen.themaLicht': 'Light',
  'instellingen.themaDonker': 'Dark',
  'instellingen.themaSysteem': 'Follow my system',
  'instellingen.auditTrail': 'What has happened?',
  'instellingen.auditUitleg': 'Every important action is recorded and cannot be changed afterwards.',
  'instellingen.auditControle': 'Verify the audit trail',
  'instellingen.auditOngeschonden': 'The audit trail is complete and unchanged.',
  'instellingen.uitnodigen': 'Invite someone',
  'instellingen.rol': 'Role',
  'instellingen.rolWijzigen': 'Change role',
  'algemeen.acties': 'Actions',
  'instellingen.nieuweLink': 'New link',
  'instellingen.uitnodigingTot': 'valid until',
  'instellingen.intrekkenVraag': 'Revoke access for',
  'instellingen.intrekkenUitleg': 'This person loses access to these books. Whatever they posted stays, with their name in the audit trail. You can invite them again later.',
  'instellingen.toegangIntrekken': 'Revoke access',
  'instellingen.perioden': 'Periods',
  'instellingen.periodeBlokkeren': 'Lock',
  'instellingen.periodeSluiten': 'Close',
  'instellingen.periodeHeropenen': 'Reopen',
  'instellingen.heropenReden': 'Why does this period need to be reopened?',

  'rol.owner': 'Owner',
  'rol.admin': 'Administrator',
  'rol.bookkeeper': 'Bookkeeper',
  'rol.accountant': 'Accountant',
  'rol.employee': 'Employee',
  'rol.viewer': 'Viewer',
  'rol.support': 'Support',

  'fout.validation_failed': 'Something is not right in the details you entered.',
  'fout.unauthenticated': 'You are no longer signed in.',
  'fout.mfa_required': 'A second step is still needed.',
  'fout.forbidden': 'You do not have permission for this.',
  'fout.not_found': 'This does not exist (any more).',
  'fout.version_conflict': 'Someone else changed this in the meantime.',
  'fout.duplicate_document': 'This document already exists.',
  'fout.period_closed': 'This period is closed.',
  'fout.entry_not_balanced': 'The entry does not balance.',
  'fout.entry_immutable': 'This entry is final and cannot be changed.',
  'fout.invoice_requirements_missing': 'The invoice is missing legally required details.',
  'fout.limit_reached': 'You have reached the limit of your plan.',
  'fout.rate_limited': 'Too many requests in a row. Please try again shortly.',
  'fout.internal_error': 'Something went wrong on our side.',
  'fout.netwerk': 'We could not reach the server. Please check your connection.',
};
